import { ReactNode } from "react";
import { ArrowLeft, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export type PageHeaderAction = {
  label: string;
  onClick: () => void;
  variant?: React.ComponentProps<typeof Button>["variant"];
  disabled?: boolean;
  icon?: ReactNode;
  destructive?: boolean;
  /**
   * True while this action's own request is in flight: the icon becomes a spinner and the button
   * stops accepting clicks. A header action that fires directly (Activate) has no dialog to hold
   * open, so without this the button looked idle and a second click sent a second request.
   */
  pending?: boolean;
};

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  /** Escape hatch for controls `actions` cannot express (dropdowns, links, selects). */
  action?: ReactNode;
  actions?: PageHeaderAction[];
  /** Back link, rendered above the title. */
  back?: { label: string; onClick: () => void };
  /** Rendered before the title block (icon, logo, avatar). */
  leading?: ReactNode;
  /** Rendered next to the title (status badges). */
  titleAdornment?: ReactNode;
  /** Extra classes for the title, e.g. `font-mono` for code-like titles. */
  titleClassName?: string;
}

const MAX_PRIMARY = 3;

export function PageHeader({
  title,
  subtitle,
  action,
  actions = [],
  back,
  leading,
  titleAdornment,
  titleClassName,
}: PageHeaderProps) {
  const primary = actions.slice(0, MAX_PRIMARY);
  const overflow = actions.slice(MAX_PRIMARY);

  return (
    // Spacing below the header comes from the page's own `space-y-*` wrapper.
    <div>
      {back && (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 mb-4"
          onClick={back.onClick}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {back.label}
        </Button>
      )}

      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          {leading}
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1
                className={cn(
                  // 20/24px semibold — matches the client console's PageHeader.
                  "text-xl font-semibold tracking-tight md:text-2xl",
                  titleClassName,
                )}
              >
                {title}
              </h1>
              {titleAdornment}
            </div>
            {subtitle && (
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {primary.map((a) => (
            <Button
              key={a.label}
              variant={a.variant ?? (a.destructive ? "destructive" : "default")}
              size="sm"
              disabled={a.disabled || a.pending}
              onClick={a.onClick}
            >
              {/* Swapped rather than added, so the button keeps its width while it works. */}
              {a.pending ? <Spinner className="mr-2" /> : a.icon}
              {a.label}
            </Button>
          ))}
          {overflow.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="More actions">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {overflow.map((a) => (
                  <DropdownMenuItem
                    key={a.label}
                    disabled={a.disabled || a.pending}
                    className={
                      a.destructive ? "text-destructive-ink" : undefined
                    }
                    onClick={a.onClick}
                  >
                    {a.pending ? <Spinner className="mr-2" /> : a.icon}
                    {a.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {action}
        </div>
      </div>
    </div>
  );
}
